// ABOUTME: Catalog-style configurator action shared by model-selection and the list widgets.
// ABOUTME: Opens the chained configurator on a prototype and saves to the page offer, the cart, or a new offer.

import type { TUserAssignment } from '@coolshop/cool-configurator';
import { useCartContext } from '@src/_new/modules/cpq/components/Cart/CartContext';
import { useAddToCart } from '@src/_new/modules/cpq/components/Cart/useAddToCart';
import {
    ChainPersistArgs,
    useChainedConfiguratorFlow,
} from '@src/_new/modules/cpq/hooks/useChainedConfiguratorFlow';
import { useOffer } from '@src/_new/modules/cpq/hooks/useOffer';
import type { ChainStep } from '@src/_new/modules/cpq/types';
import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

interface AddProductArgs {
    productId: string;
    configurationJson?: string;
    chain?: ChainStep[];
}

interface UseCatalogConfiguratorActionInput {
    /** Offer node the item is added to (a detail page's node). Absent: the cart, or a new offer. */
    offerNodeId?: string;
    /** Called once each time the configurator drawer closes, whether saved or dismissed. */
    onClose?: () => void;
}

export function useCatalogConfiguratorAction({
    offerNodeId,
    onClose,
}: UseCatalogConfiguratorActionInput = {}) {
    const { formatMessage } = useIntl();
    const navigate = useNavigate();
    const { addLineItem, createOffer } = useOffer();
    const cart = useCartContext();
    // Read through a ref so `addProduct` keeps its identity while the cart reloads, and the catalogue
    // it is handed to is not re-rendered by every cart change.
    const cartRef = useRef(cart);
    cartRef.current = cart;
    const { addToCart } = useAddToCart();

    // True while a finalized configuration is being saved, so a loading backdrop covers the gap
    // between closing the configurator and the navigation/refresh that follows.
    const [isSaving, setIsSaving] = useState(false);

    // Adds a product to the page-node offer, to the active cart, or to a freshly created offer.
    // The offer is created lazily — only once there is a real product to add. The configuration is
    // only sent for a configured product, so a plain add sends exactly what it did before.
    const addProduct = useCallback(
        async ({ productId, configurationJson, chain }: AddProductArgs) => {
            const configuration = configurationJson ? { configurationJson, chain } : undefined;
            const lineItemAdded = formatMessage({
                id: 'custom-pages.action-bar.add-offeritem-success',
                defaultMessage: 'Line item added successfully',
            });

            if (offerNodeId) {
                await addLineItem(offerNodeId, { productId, quantity: 1, ...configuration });
                SnackBarUtils.success(lineItemAdded);
                navigate(0);
                return;
            }

            // When the cart feature is enabled, add the item to the active cart (status 'cart')
            // instead of creating a draft offer. Feedback (in progress, success, error) is shown by
            // useAddToCart; on failure it has already reported the error, so the
            // drawer just stays closed. The drawer opens only on the first add: once the user has
            // closed it, later adds just show the toast.
            const currentCart = cartRef.current;
            if (currentCart?.enabled) {
                // Captured before the add: if the cart is discarded or converted while the add is in
                // flight, the drawer must not auto-open for the cart it no longer belongs to.
                const generation = currentCart.getCartGeneration();
                if (await addToCart(productId, 1, configuration)) {
                    currentCart.openDrawerOnFirstAdd(generation);
                }
                return;
            }

            const { offerId } = await createOffer({
                title: formatMessage({
                    id: 'cpq.offer-list.default-title',
                    defaultMessage: 'New Offer',
                }),
                customerName: '',
            });
            await addLineItem(offerId, { productId, quantity: 1, ...configuration });
            SnackBarUtils.success(lineItemAdded);
            navigate(`/offers/${offerId}`);
        },
        [offerNodeId, addToCart, addLineItem, createOffer, formatMessage, navigate],
    );

    const persistChain = useCallback(
        ({ chain, lastStep }: ChainPersistArgs) =>
            addProduct({
                productId: lastStep.productId,
                configurationJson: lastStep.configurationJson,
                chain,
            }),
        [addProduct],
    );

    // The catalog has no offer/client context, so a save persists immediately (the default gate).
    const chainedFlow = useChainedConfiguratorFlow({ persist: persistChain, setBusy: setIsSaving });
    const { openAdd, session } = chainedFlow;

    const openConfigurator = useCallback(
        (prototypeId: string, initialUserAssignments?: TUserAssignment[]) =>
            openAdd({ prototypeId, initialUserAssignments }),
        [openAdd],
    );

    // Fires onClose on the open → closed transition only, not on the first render.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (session) {
            wasOpenRef.current = true;
            return;
        }
        if (wasOpenRef.current) {
            wasOpenRef.current = false;
            onClose?.();
        }
    }, [session, onClose]);

    return { chainedFlow, isSaving, setIsSaving, openConfigurator, addProduct };
}
